import {
  newBCategoryIdConfig,
  newBProductIdConfig,
  newBTopicIdConfig,
  newChannelsConfig,
  newCountryIdConfig,
  newEventConfig,
  newInquirerIdConfig,
  newLanguageIdConfig,
  newSpecializationsConfig,
  newStringArrayConfig,
  newTeamNameConfig,
  newTerritoryConfig,
  newUserIdConfig,
  newYesNoConfig,
} from 'app/common/common/search-select/search-select.config';
import { SearchOrigin } from 'app/common/common/enums/search.enum';
import {
  PERM_VIEW_ENQUIRER,
  PERM_VIEW_ENQUIRY_ANSWER,
  PERM_VIEW_ENQUIRY_ASSIGNEE,
  PERM_VIEW_ENQUIRY_EDITED,
  PERM_VIEW_ENQUIRY_INSERTED,
  PERM_VIEW_ENQUIRY_QUESTION,
} from 'app/common/common/constants/permissions';
import { PermissionHandler } from 'app/auth/auth.service';

export enum Type {
  DROPDOWN = 'dropdown',
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
}

export enum FConfigs {
  INTERACTION = 'id',
  QUESTION = 'question_id',
  INT_COMM_DATE = 'internal_communication_date',
  INT_COMM_TEXT = 'internal_communication_text',
  QUESTION_TEXT = 'question_text',
  ANSWER_TEXT = 'answer_text',
  FOLLOW_UP_TEXT = 'follow_up_text',
  CREATED_ON = 'created_on',
  EDITED_ON = 'edited_on',
  DUE_DATE = 'due_date',
  FOLLOW_SENT_DATE = 'follow_up_sent_date',
  FOLLOW_RECEIVED_DATE = 'follow_up_received_date',
  ANSWER_DATE = 'answer_date',
  CLOSED_ON_DATE = 'closed_on_date',
  REOPENED_DATE = 'reopened_date',
  COUNTRY = 'country',
  PRODUCT = 'product',
  INQUIRER = 'inquirer',
  INSERTED_BY = 'inserted_by',
  LAST_EDITED_BY = 'edited_by',
  ASSIGNED_TO = 'assigned_to',
  ASSIGNED_TO_ANY = 'assigned_to_any',
  ANSWERED_BY = 'answered_by',
  APPROVED_BY = 'approved_by',
  INT_COMM_USER = 'internal_communication_user',
  CATEGORY = 'category',
  TOPIC = 'topic',
  TEAM = 'team',
  MY_TEAM = 'my_teams',
  STATUS = 'status',
  ANSWER = 'answer_unavailable',
  SRD = 'srd',
  ADR = 'adr_related',
  ADR_REF_IDS = 'adr_related_ref_ids',
  PQC = 'pqc',
  PQC_REF_IDS = 'pqc_ref_ids',
  INCOMING = 'incoming_channel',
  OUTGOING = 'outgoing_channel',
  EVENT = 'event',
  LANGUAGE = 'language',
  OFF_LABEL = 'off_label',
  HCP = 'hcp',
  NON_HCP = 'non_hcp',
  KEE = 'kee',
  HOSPITAL = 'hospital',
  CITY = 'city',
  INSTITUTION = 'institution',
  TERRITORY = 'territory',
  SELECT_ALL = 'selectAll',
  PRIORITY = 'priority',
  ID = 'id',
  INITIAL_QUESTION_TEXT = 'initial_question_text',
  TEXT = 'text',
  INTERACTION_REF_IDS = 'additional_interaction_ref_ids',
  TYPE = 'type',
}

const selectorToPermission: { [selector: string]: string } = {
  [FConfigs.INT_COMM_TEXT]: PERM_VIEW_ENQUIRY_ANSWER,
  [FConfigs.QUESTION_TEXT]: PERM_VIEW_ENQUIRY_QUESTION,
  [FConfigs.ANSWER_TEXT]: PERM_VIEW_ENQUIRY_ANSWER,
  [FConfigs.FOLLOW_UP_TEXT]: PERM_VIEW_ENQUIRY_QUESTION,
  [FConfigs.INQUIRER]: PERM_VIEW_ENQUIRER,
  [FConfigs.INSERTED_BY]: PERM_VIEW_ENQUIRY_INSERTED,
  [FConfigs.LAST_EDITED_BY]: PERM_VIEW_ENQUIRY_EDITED,
  [FConfigs.ASSIGNED_TO]: PERM_VIEW_ENQUIRY_ASSIGNEE,
  [FConfigs.ASSIGNED_TO_ANY]: PERM_VIEW_ENQUIRY_ASSIGNEE,
  [FConfigs.ANSWERED_BY]: PERM_VIEW_ENQUIRY_EDITED,
  [FConfigs.APPROVED_BY]: PERM_VIEW_ENQUIRY_EDITED,
  [FConfigs.INT_COMM_USER]: PERM_VIEW_ENQUIRY_EDITED,
  [FConfigs.INITIAL_QUESTION_TEXT]: PERM_VIEW_ENQUIRY_QUESTION,
  [FConfigs.TEXT]: PERM_VIEW_ENQUIRY_QUESTION,
};

export class FilterSelection {
  value?: any;
  selected?: string;
  multiple?: boolean;
  checked?: boolean;
  translate?: boolean;

  private constructor(
    public id: number,
    public name: string,
    public selector: FConfigs,
    public type: Type
  ) {}

  get exportName(): string {
    if (this.name === 'COLUMN_HCP_SPEC') return 'INQUIRER_TYPE';
    if (this.name === 'COLUMN_NON_HCP_SPEC') return 'SPECIALIZATION';
    return this.name;
  }

  private multi(): this {
    this.multiple = true;
    return this;
  }

  private trans(): this {
    this.translate = true;
    return this;
  }

  static getFilters(): FilterSelection[] {
    return [
      new this(1, 'COLUMN_INTERACTION_ID', FConfigs.INTERACTION, Type.NUMBER),
      new this(2, 'COLUMN_QUESTION_ID', FConfigs.QUESTION, Type.NUMBER),
      new this(46, 'QUESTION_TEXT', FConfigs.QUESTION_TEXT, Type.TEXT),
      new this(3, 'COLUMN_CREATED_ON', FConfigs.CREATED_ON, Type.DATE),
      new this(4, 'COLUMN_DUE_DATE', FConfigs.DUE_DATE, Type.DATE),
      new this(5, 'COLUMN_FOLLOW_SENT_DATE', FConfigs.FOLLOW_SENT_DATE, Type.DATE),
      new this(6, 'COLUMN_FOLLOW_RECEIVED_DATE', FConfigs.FOLLOW_RECEIVED_DATE, Type.DATE),
      new this(45, 'FOLLOW_UP_TEXT', FConfigs.FOLLOW_UP_TEXT, Type.TEXT),
      new this(8, 'COLUMN_CLOSED_ON_DATE', FConfigs.CLOSED_ON_DATE, Type.DATE),
      new this(9, 'COLUMN_REOPENED_DATE', FConfigs.REOPENED_DATE, Type.DATE),
      new this(10, 'COUNTRY', FConfigs.COUNTRY, Type.DROPDOWN).multi(),
      new this(11, 'PRODUCT', FConfigs.PRODUCT, Type.DROPDOWN).multi(),
      new this(12, 'INQUIRER', FConfigs.INQUIRER, Type.DROPDOWN).multi(),
      new this(13, 'COLUMN_INSERTED_BY', FConfigs.INSERTED_BY, Type.DROPDOWN).multi(),
      new this(14, 'COLUMN_LAST_EDITED_BY', FConfigs.LAST_EDITED_BY, Type.DROPDOWN).multi(),
      new this(48, 'COLUMN_LAST_EDITED_DATE', FConfigs.EDITED_ON, Type.DATE),
      new this(17, 'Assigned_to_Any_Time', FConfigs.ASSIGNED_TO_ANY, Type.DROPDOWN).multi(),
      new this(51, 'Assigned_To', FConfigs.ASSIGNED_TO, Type.DROPDOWN).multi(),
      new this(7, 'COLUMN_ANSWER_DATE', FConfigs.ANSWER_DATE, Type.DATE),
      new this(19, 'COLUMN_ANSWERED_BY', FConfigs.ANSWERED_BY, Type.DROPDOWN).multi(),
      new this(28, 'ANSWER_UNAVAILABLE', FConfigs.ANSWER, Type.DROPDOWN).trans(),
      new this(47, 'ANSWER_TEXT', FConfigs.ANSWER_TEXT, Type.TEXT),
      new this(20, 'COLUMN_APPROVED_BY', FConfigs.APPROVED_BY, Type.DROPDOWN).multi(),
      new this(23, 'Category', FConfigs.CATEGORY, Type.DROPDOWN).multi(),
      new this(24, 'TOPIC', FConfigs.TOPIC, Type.DROPDOWN).multi(),
      new this(25, 'TEAM', FConfigs.TEAM, Type.DROPDOWN).multi(),
      new this(27, 'Status', FConfigs.STATUS, Type.DROPDOWN).multi(),
      new this(29, 'SRD_NEEDED', FConfigs.SRD, Type.DROPDOWN).trans(),
      new this(30, 'ADR_RELATED', FConfigs.ADR, Type.DROPDOWN).trans(),
      new this(31, 'COLUMN_PQC_RELATED', FConfigs.PQC, Type.DROPDOWN).trans(),
      new this(32, 'COLUMN_INCOMING_CHANNEL', FConfigs.INCOMING, Type.DROPDOWN).multi(),
      new this(33, 'COLUMN_OUTGOING_CHANNEL', FConfigs.OUTGOING, Type.DROPDOWN).multi(),
      new this(34, 'Event', FConfigs.EVENT, Type.DROPDOWN).multi(),
      new this(35, 'LANGUAGE', FConfigs.LANGUAGE, Type.DROPDOWN).multi(),
      new this(36, 'COLUMN_OFF_LABEL', FConfigs.OFF_LABEL, Type.DROPDOWN).trans(),
      new this(37, 'COLUMN_HCP_SPEC', FConfigs.HCP, Type.DROPDOWN).multi(),
      new this(38, 'COLUMN_NON_HCP_SPEC', FConfigs.NON_HCP, Type.DROPDOWN).multi(),
      new this(39, 'COLUMN_KEE', FConfigs.KEE, Type.DROPDOWN).trans(),
      new this(40, 'COLUMN_HOSPITAL_BASED', FConfigs.HOSPITAL, Type.DROPDOWN).trans(),
      new this(41, 'COLUMN_CITY', FConfigs.CITY, Type.TEXT),
      new this(42, 'COLUMN_INSTITUTION', FConfigs.INSTITUTION, Type.TEXT),
      new this(43, 'COLUMN_TERRITORY', FConfigs.TERRITORY, Type.DROPDOWN).multi(),
      new this(21, 'COLUMN_INT_COMM_USER', FConfigs.INT_COMM_USER, Type.DROPDOWN).multi(),
      new this(22, 'COLUMN_INT_COMM_DATE', FConfigs.INT_COMM_DATE, Type.DATE),
      new this(44, 'INT_COMM_TEXT', FConfigs.INT_COMM_TEXT, Type.TEXT),
      new this(55, 'COLUMN_INIT_QUESTION_TEXT', FConfigs.INITIAL_QUESTION_TEXT, Type.TEXT),
      new this(56, 'ADR_REF_IDS', FConfigs.ADR_REF_IDS, Type.TEXT),
      new this(57, 'PQC_REF_IDS', FConfigs.PQC_REF_IDS, Type.TEXT),
      new this(58, 'INTERACTION_REF_IDS', FConfigs.INTERACTION_REF_IDS, Type.TEXT),
    ];
  }

  static getFiltersSorted(trans: (v: string) => string, h: PermissionHandler): FilterSelection[] {
    return this.getFilters()
      .filter((f) => this.hasPermission(f.selector, h))
      .sort((a, b) => (trans(a.name) < trans(b.name) ? -1 : 1));
  }

  static getFiltersForReset(): FilterSelection[] {
    const allFilters = this.getFilters();
    allFilters.push(new this(50, 'ASSIGNED_TO', FConfigs.ASSIGNED_TO, Type.NUMBER));
    allFilters.push(new this(51, 'PRIORITY', FConfigs.PRIORITY, Type.TEXT));
    allFilters.push(new this(52, 'ID', FConfigs.ID, Type.NUMBER));
    allFilters.push(new this(53, 'Search_inquiries', FConfigs.TEXT, Type.TEXT));
    return allFilters;
  }

  static getViewExportColumns(translate: (val: string) => string): FilterSelection[] {
    const allColumns = FilterSelection.getExportColumns();
    allColumns.sort((a, b) => (translate(a.exportName) < translate(b.exportName) ? -1 : 1));
    allColumns.unshift(new this(0, 'SELECT_ALL', FConfigs.SELECT_ALL, Type.TEXT));
    allColumns.forEach((c) => (c.checked = false));
    return allColumns;
  }

  static getExportColumns(): FilterSelection[] {
    const allColumns = FilterSelection.getFilters();
    allColumns.push(new this(-1, 'TYPE', FConfigs.TYPE, Type.TEXT));
    return allColumns;
  }

  static adjustValue(selector: string, term: string, permHandler: PermissionHandler): string {
    return !term ? '' : !this.hasPermission(selector, permHandler) ? '*****' : term;
  }

  private static hasPermission(selector: string, permHandler: PermissionHandler): boolean {
    const permission = selectorToPermission[selector];
    return !permission || permHandler.has(permission);
  }
}

const filterSelection: Array<FilterSelection> = FilterSelection.getFilters();

export const multipleFilterSelectionCount = filterSelection.filter((f) => f.multiple).length;

export const filterConfigLabels = Object.assign(
  {
    [FConfigs.PRIORITY]: 'PRIORITY',
  },
  {
    [FConfigs.TEXT]: 'Search_inquiries',
  },
  ...filterSelection.map((object) => {
    return { [object.selector]: object.name };
  })
);

export const filterConfigTypes = Object.assign(
  {},
  ...filterSelection.map((object) => {
    return { [object.selector]: object.type };
  })
);

export const filterConfigMultiples = Object.assign(
  {},
  ...filterSelection.map((object) => {
    return { [object.selector]: object.multiple };
  })
);

export const filterConfigTranslate = Object.assign(
  {},
  ...filterSelection.map((object) => {
    return { [object.selector]: object.translate };
  })
);

export const filterConfigMapping = {
  [FConfigs.COUNTRY]: newCountryIdConfig([]),
  [FConfigs.PRODUCT]: newBProductIdConfig([], filterConfigLabels[FConfigs.PRODUCT]),
  [FConfigs.INQUIRER]: newInquirerIdConfig([], filterConfigLabels[FConfigs.INQUIRER]),
  [FConfigs.INSERTED_BY]: newUserIdConfig([], filterConfigLabels[FConfigs.INSERTED_BY]),
  [FConfigs.LAST_EDITED_BY]: newUserIdConfig([], filterConfigLabels[FConfigs.LAST_EDITED_BY]),
  [FConfigs.ASSIGNED_TO]: newUserIdConfig([], filterConfigLabels[FConfigs.ASSIGNED_TO]),
  [FConfigs.ASSIGNED_TO_ANY]: newUserIdConfig([], filterConfigLabels[FConfigs.ASSIGNED_TO_ANY]),
  [FConfigs.ANSWERED_BY]: newUserIdConfig([], filterConfigLabels[FConfigs.ANSWERED_BY]),
  [FConfigs.APPROVED_BY]: newUserIdConfig([], filterConfigLabels[FConfigs.APPROVED_BY]),
  [FConfigs.INT_COMM_USER]: newUserIdConfig([], filterConfigLabels[FConfigs.INT_COMM_USER]),
  [FConfigs.CATEGORY]: newBCategoryIdConfig([], filterConfigLabels[FConfigs.CATEGORY]),
  [FConfigs.TOPIC]: newBTopicIdConfig([]),
  [FConfigs.TEAM]: newTeamNameConfig([], ''),
  [FConfigs.MY_TEAM]: newYesNoConfig(filterConfigLabels[FConfigs.MY_TEAM]),
  [FConfigs.STATUS]: newStringArrayConfig([], filterConfigLabels[FConfigs.STATUS]),
  [FConfigs.ANSWER]: newYesNoConfig(filterConfigLabels[FConfigs.ANSWER]),
  [FConfigs.SRD]: newYesNoConfig(filterConfigLabels[FConfigs.SRD]),
  [FConfigs.ADR]: newYesNoConfig(filterConfigLabels[FConfigs.ADR]),
  [FConfigs.PQC]: newYesNoConfig(filterConfigLabels[FConfigs.PQC]),
  [FConfigs.INCOMING]: newChannelsConfig([], filterConfigLabels[FConfigs.INCOMING]),
  [FConfigs.OUTGOING]: newChannelsConfig([], filterConfigLabels[FConfigs.OUTGOING]),
  [FConfigs.EVENT]: newEventConfig([], filterConfigLabels[FConfigs.EVENT]),
  [FConfigs.LANGUAGE]: newLanguageIdConfig([]),
  [FConfigs.OFF_LABEL]: newYesNoConfig(filterConfigLabels[FConfigs.OFF_LABEL]),
  [FConfigs.HCP]: newSpecializationsConfig([], filterConfigLabels[FConfigs.HCP]),
  [FConfigs.NON_HCP]: newSpecializationsConfig([], filterConfigLabels[FConfigs.NON_HCP]),
  [FConfigs.KEE]: newYesNoConfig(filterConfigLabels[FConfigs.KEE]),
  [FConfigs.HOSPITAL]: newYesNoConfig(filterConfigLabels[FConfigs.HOSPITAL]),
  [FConfigs.TERRITORY]: newTerritoryConfig([]),
};
export const filterConfigDefault = {
  [FConfigs.MY_TEAM]: 'yes',
  [FConfigs.ANSWER]: 'yes',
  [FConfigs.SRD]: 'yes',
  [FConfigs.ADR]: 'yes',
  [FConfigs.PQC]: 'yes',
  [FConfigs.OFF_LABEL]: 'yes',
  [FConfigs.KEE]: 'yes',
  [FConfigs.HOSPITAL]: 'yes',
};

export class Query {
  value?: string | any[];
  displayTerm?: string;
  adjustedTerm?: string;
  origin?: SearchOrigin; // Used only by the filters sync

  constructor(public field: string, public join?: string) {}

  setValueAndDisplayTerm(value: string | any[], displayTerm: string, adjustedTerm?: string): void {
    this.value = value;
    this.displayTerm = displayTerm;
    this.adjustedTerm = adjustedTerm || displayTerm;
  }
}

export enum FilterModalConfig {
  SHARE = 'share',
  SAVE = 'save',
  SAVEANDSHARE = 'saveAndShare',
  DELETE = 'delete',
  CLOSE = 'close',
}

export const allExports = filterSelection.reduce((previousValue, currentValue) => {
  previousValue[currentValue['selector']] = true;
  return previousValue;
}, {});
